import {
  EuiButton,
  EuiButtonEmpty,
  EuiComboBox,
  EuiForm,
  EuiFormRow,
  EuiLink,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
} from '@elastic/eui';
import type { MouseEventHandler } from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import type { Fund, FundConflatedAsset } from '../model';
import { getDomicileURL, settingsSavePortfolio } from '../model';
import { PageContext } from '../page_container';

export interface SaveToPortfolioModalParams {
  item: Fund | FundConflatedAsset;
  onClose: () => void;
}

export function AddToPortfolioModal({ item, onClose }: SaveToPortfolioModalParams) {
  const navigate = useNavigate();
  const { settings, setSettings, addToast, domicileCode } = useContext(PageContext);
  const existingPortfolios = useMemo(
    () => settings.portfolios.map((portfolio) => ({ label: portfolio.name })),
    [settings],
  );

  const [selectedPortfolios, setSelectedPortfolios] = useState<Array<{ label: string }>>([]);
  const onCreateNewPortfolio = (searchValue = '') => {
    const normalizedSearchValue = searchValue.trim();
    if (!normalizedSearchValue) {
      return;
    }

    setSelectedPortfolios([...selectedPortfolios, { label: normalizedSearchValue }]);
  };

  const onNavigateToPortfoliosPage: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    navigate(
      getDomicileURL(
        domicileCode,
        selectedPortfolios.length === 1
          ? `/portfolios/${encodeURIComponent(selectedPortfolios[0].label)}`
          : '/portfolios',
      ),
    );

    onClose();
  };

  const onCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const addToPortfolios = () => {
    for (const { label: name } of selectedPortfolios) {
      setSettings(settingsSavePortfolio(settings, name, item));
    }

    const title =
      selectedPortfolios.length > 1 ? (
        <>
          Your{' '}
          <EuiLink href={getDomicileURL(domicileCode, '/portfolios')} onClick={onNavigateToPortfoliosPage}>
            portfolios
          </EuiLink>{' '}
          were updated
        </>
      ) : (
        <>
          Your{' '}
          <EuiLink
            href={getDomicileURL(domicileCode, `/portfolios/${encodeURIComponent(selectedPortfolios[0].label)}`)}
            onClick={onNavigateToPortfoliosPage}
          >
            portfolio
          </EuiLink>{' '}
          was updated
        </>
      );

    addToast({
      id: `updated-portfolio-${Date.now()}`,
      iconType: 'submodule',
      color: 'success',
      title,
    });

    setSelectedPortfolios([]);

    onClose();
  };

  return (
    <EuiModal onClose={onCloseModal}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Add to portfolio</EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiForm id="save-selection-form" component="form">
          <EuiFormRow helpText="Select an existing portfolio, or create a new one.">
            <EuiComboBox
              placeholder="Type portfolio name"
              options={existingPortfolios}
              selectedOptions={selectedPortfolios}
              onChange={setSelectedPortfolios}
              onCreateOption={onCreateNewPortfolio}
              customOptionText="Save {searchValue} as a new portfolio"
            />
          </EuiFormRow>
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty
          href={getDomicileURL(domicileCode, '/portfolios')}
          iconType={'submodule'}
          onClick={onNavigateToPortfoliosPage}
        >
          Manage portfolios
        </EuiButtonEmpty>
        <EuiButtonEmpty onClick={onCloseModal}>Cancel</EuiButtonEmpty>
        <EuiButton
          type="submit"
          form="save-selection-form"
          fill
          onClick={addToPortfolios}
          isDisabled={selectedPortfolios.length === 0}
        >
          Add
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
}
